import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { supabase } from '../../lib/supabaseClient';
import { Alert, AlertDescription } from '../ui/alert';

const ClientQuestionsManager = ({ clientId }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, [clientId]);

  const fetchQuestions = async () => {
    try {
      const { data, error } = await supabase
        .from('client_questions')
        .select('*')
        .eq('client_id', clientId)
        .order('display_order', { ascending: true });

      if (error) throw error;

      setQuestions(data || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setMessage({ type: 'error', text: 'Failed to load questions' });
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `temp-${Date.now()}`, // Temporary ID for new questions
      client_id: clientId,
      question_text: '',
      is_required: true,
      display_order: questions.length,
      isNew: true,
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const deleteQuestion = async (index) => {
    const question = questions[index];

    // If it's a new unsaved question, just remove from state
    if (question.isNew) {
      setQuestions(questions.filter((_, i) => i !== index));
      return;
    }

    // Otherwise, delete from database
    try {
      const { error } = await supabase
        .from('client_questions')
        .delete()
        .eq('id', question.id);

      if (error) throw error;

      setQuestions(questions.filter((_, i) => i !== index));
      setMessage({ type: 'success', text: 'Question deleted successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting question:', error);
      setMessage({ type: 'error', text: 'Failed to delete question' });
    }
  };

  const saveQuestions = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Separate new and existing questions
      const newQuestions = questions.filter(q => q.isNew);
      const existingQuestions = questions.filter(q => !q.isNew);

      // Insert new questions
      if (newQuestions.length > 0) {
        const insertData = newQuestions.map((q, index) => ({
          client_id: clientId,
          question_text: q.question_text,
          is_required: q.is_required,
          display_order: questions.indexOf(q),
        }));

        const { error: insertError } = await supabase
          .from('client_questions')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      // Update existing questions
      for (const question of existingQuestions) {
        const { error } = await supabase
          .from('client_questions')
          .update({
            question_text: question.question_text,
            is_required: question.is_required,
            display_order: questions.indexOf(question),
            updated_at: new Date().toISOString(),
          })
          .eq('id', question.id);

        if (error) throw error;
      }

      setMessage({ type: 'success', text: 'Questions saved successfully!' });
      setTimeout(() => setMessage(null), 3000);

      // Refresh questions from database
      await fetchQuestions();
    } catch (error) {
      console.error('Error saving questions:', error);
      setMessage({ type: 'error', text: 'Failed to save questions: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newQuestions = [...questions];
    const draggedItem = newQuestions[draggedIndex];
    newQuestions.splice(draggedIndex, 1);
    newQuestions.splice(index, 0, draggedItem);

    setQuestions(newQuestions);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <Card className="w-full max-w-full overflow-hidden">
      <CardHeader>
        <CardTitle>Custom Application Questions</CardTitle>
        <CardDescription>
          Add custom questions that will appear on job applications for this client.
          Questions can be toggled on/off for specific RFQs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {questions.map((question, index) => (
            <div
              key={question.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-start gap-3 p-3 border rounded-lg ${
                draggedIndex === index ? 'opacity-50' : ''
              } bg-white hover:bg-gray-50 cursor-move`}
            >
              <div className="cursor-grab active:cursor-grabbing mt-2">
                <GripVertical className="h-5 w-5 text-gray-400" />
              </div>

              <div className="flex-1 space-y-2">
                <Input
                  placeholder="Enter your question here..."
                  value={question.question_text}
                  onChange={(e) => updateQuestion(index, 'question_text', e.target.value)}
                  className="w-full"
                />

                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={question.is_required}
                      onChange={(e) => updateQuestion(index, 'is_required', e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Required field
                  </label>
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => deleteQuestion(index)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          {questions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No custom questions yet. Click "Add Question" to get started.
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={addQuestion}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Question
          </Button>

          <Button
            onClick={saveQuestions}
            disabled={saving || questions.length === 0}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Questions'}
          </Button>
        </div>

        <div className="text-xs text-gray-500 mt-4">
          <p><strong>Note:</strong> Drag questions to reorder them. They will appear in this order on application forms.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientQuestionsManager;
